'use client';

import { useState, useRef } from 'react';
import { SocialMediaIconsList } from '@/src/components/ui/SocialMediaIconsList';
import { IUITile } from '@/src/types/ITile';
import { useEditingHoverControl } from '../../../hooks/useEditingHoverControl';
import { useProfileUIStore } from '../../../store';
import { Icon, useToast } from '@/src/components/ui';
import { useClickOrDrag } from '../../../hooks/useClickOrDrag';
import { createTile, editTile } from '@/src/actions/tiles';
import { useEditableTextarea } from '@/src/hooks/use-editable-textarea';
import { getTileEditStatus } from '@/src/utils/helpers';
import { Textarea } from '@workspace/ui/components/textarea';
import { Input } from '@workspace/ui/components/input';
import { cn } from '@workspace/ui/lib/utils';
import { parseSocialMediaUrl } from '@/src/utils/parseSocialMediaUrl';
import { noBorderStyles } from '@/src/constants/constants';
import { useTilesStore } from '../../../store/tiles-store';
import { LoadingOverlay } from '../LoadingOverlay';

type Props = {
  isMyProfile?: boolean;
  tile: IUITile;
  profileAvatar?: string;
  profileName: string;
  touchedTileId: string | null;
  setTouchedTileId: (id: string | null) => void;
  onHoverStateChange?: (shouldDisable: boolean) => void;
};

const TITLE_EDITABLE_PLATFORMS = ['steam', 'discord'];

const socialMediaStyles = {
  instagram: {
    background: 'instagram-gradient',
    textColor: 'text-white',
  },
  discord: {
    background: 'bg-[#5865F1]',
    textColor: 'text-white',
  },
  bluesky: {
    background: 'bg-[#0387FE]',
    textColor: 'text-white',
  },
  steam: {
    background: 'steam-gradient',
    textColor: 'text-white',
  },
  itch: {
    background: 'bg-[#FF244A]',
    textColor: 'text-white',
  },
  twitch: {
    background: 'bg-[#9147FF]',
    textColor: 'text-white',
  },
  default: {
    background: 'bg-gradient-to-br from-gray-600 to-gray-800',
    textColor: 'text-white',
  },
};

export const SocialTile = ({
  isMyProfile,
  tile,
  onHoverStateChange,
  touchedTileId,
  setTouchedTileId,
}: Props) => {
  const is2x2 = tile.w === 2 && tile.h === 6;
  const is2x1 = tile.w === 2 && tile.h === 3;
  const is1x1 = tile.w === 1 && tile.h === 3;
  const previewMode = useProfileUIStore((state) => state.previewMode);

  const { handleStartEditing, handleStopEditing } =
    useEditingHoverControl(onHoverStateChange);

  const updateTile = useTilesStore((state) => state.updateTile);
  const toast = useToast();
  const setTileLoading = useTilesStore((state) => state.setTileLoading);
  const isMobileView = useProfileUIStore((state) => state.isMobileView);

  const [editTitle, setEditTitle] = useState('');
  const [isTitleDirty, setIsTitleDirty] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const canEditTitle =
    TITLE_EDITABLE_PLATFORMS.includes(tile.iconName as string) || false;

  const handleBlurWithinTile = (e: React.FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (next && wrapperRef.current?.contains(next)) return;

    handleSubmitEdit();
  };

  const {
    tempValue,
    value,
    isEditing,
    inputRef,
    handleChange,
    handleKeyDown,
    submitEdit,
    startEdit,
    cancelEdit,
  } = useEditableTextarea({
    initialValue: tile.content || '',
    onSubmit: async (tempValue) => {
      const { wasEdited, hasContent, isEmpty } = getTileEditStatus(
        tile.content ? tile.content : '',
        tempValue,
      );

      if (!isEmpty && wasEdited) {
        setTileLoading(tile.i, true);
        try {
          const result = parseSocialMediaUrl(tempValue);
          if (!result) {
            toast.error('Invalid social media link');
            return;
          }

          const { platform, username, url } = result;
          const customTitle = canEditTitle
            ? isTitleDirty
              ? (editTitle ?? '').trim() || username
              : username
            : username;

          if (canEditTitle && !isTitleDirty) {
            setEditTitle(username);
          }

          let response;
          if (!hasContent) {
            toast.updating('Adding social link');
            response = await createTile({
              ...tile,
              content: tempValue,
              iconName: platform as
                | 'instagram'
                | 'discord'
                | 'bluesky'
                | 'twitch'
                | 'steam'
                | 'itch'
                | undefined,
              title: customTitle,
            });
            if (!response) {
              toast.error('Failed to add social link');
              return;
            }
          } else {
            toast.updating('Updating social link');
            response = await editTile(
              { content: url, title: customTitle },
              tile.id as number,
            );
            if (!response) {
              toast.error('Failed to update social link');
              return;
            }
          }
          updateTile(tile.i, {
            ...tile,
            ...response,
            id: response.id,
            icon: platform,
            static: false,
          });
          toast.updated('Social link added');
        } catch (error) {
          toast.error('Failed to save social link');
        } finally {
          setTileLoading(tile.i, false);
        }
      }
      handleStopEditing();
    },
  });

  const shouldShowSaveHint = isTitleDirty || tempValue !== value;

  const { onMouseDown, onTouchStart } = useClickOrDrag(() => {
    handleStartEdit();
  });

  const handleStartEdit = () => {
    if (previewMode) return;

    const parsedUsername =
      parseSocialMediaUrl(tile.content || '')?.username || '';
    setEditTitle(tile.title || parsedUsername || '');
    setIsTitleDirty(false);

    updateTile(tile.i, { isEditing: true });
    startEdit();
    handleStartEditing();
  };

  const saveTitleOnly = async () => {
    const parsed = parseSocialMediaUrl(tile.content || '')?.username ?? '';
    const currentTitle = tile.title ?? parsed;
    const newTitle = editTitle.trim() || parsed;
    if (newTitle === currentTitle || !tile.id || !tile.content) return;
    setTileLoading(tile.i, true);
    try {
      toast.updating('Updating name');
      const response = await editTile(
        { content: tile.content, title: newTitle },
        tile.id,
      );
      if (!response) {
        toast.error('Failed to update name');
        return;
      }
      updateTile(tile.i, { ...tile, ...response });
      toast.updated('Name updated');
    } catch {
      toast.error('Failed to update name');
    } finally {
      setTileLoading(tile.i, false);
    }
  };

  const handleSubmitEdit = () => {
    const parsed = parseSocialMediaUrl(tile.content || '')?.username ?? '';
    const titleChanged =
      (editTitle.trim() || parsed) !== (tile.title ?? parsed);
    if (
      canEditTitle &&
      titleChanged &&
      tile.id &&
      tile.content &&
      tempValue === value
    ) {
      saveTitleOnly().then(() => {
        cancelEdit();
        updateTile(tile.i, { isEditing: false });
        handleStopEditing();
        setTouchedTileId(null);
      });
      return;
    }

    updateTile(tile.i, { isEditing: false });
    submitEdit();
    handleStopEditing();
    setTouchedTileId(null);
  };

  const currentStyles =
    socialMediaStyles[tile.iconName as keyof typeof socialMediaStyles] ||
    socialMediaStyles.default;

  const tileTitle = canEditTitle ? tile.title : `@${tile.title}`;

  if (isMyProfile && isEditing) {
    return (
      <div
        ref={wrapperRef}
        tabIndex={-1}
        className="relative flex flex-col items-center justify-end w-full h-full transition-colors cursor-pointer text-gray-text bg-bg-gray rounded-2xl"
      >
        {tile.isLoading && <LoadingOverlay />}
        <SocialMediaIconsList
          className={cn('absolute translate-1/2 top-1 right-1/2', {
            '-top-5 md:top-1': canEditTitle,
          })}
        />

        <div className="w-full flex flex-col md:gap-2 px-3 md:px-4 flex-1 min-h-0 justify-end">
          {canEditTitle && (
            <div className="relative w-full md:mt-10">
              <span className="absolute -top-2 left-2 bg-[#f3f3f3] px-1 text-[10px] text-gray-text">
                Add title here
              </span>

              <Input
                style={noBorderStyles}
                value={editTitle}
                maxLength={30}
                onChange={(e) => {
                  setEditTitle(e.target.value);
                  setIsTitleDirty(true);
                }}
                onBlur={handleBlurWithinTile}
                className={cn(
                  'w-full text-black text-sm font-medium bg-transparent px-2 py-1 border! border-black/10! placeholder:text-sm',
                )}
              />
              {shouldShowSaveHint && (
                <span className="absolute top-9 right-0 bg-[#f3f3f3] px-1 text-[10px] text-blue-primary">
                  Click outside to save
                </span>
              )}
            </div>
          )}

          <Textarea
            style={noBorderStyles}
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={tempValue}
            onChange={handleChange}
            onBlur={handleBlurWithinTile}
            onKeyDown={handleKeyDown}
            className={cn(
              'w-full md:h-1/2 h-fit text-black md:px-4 px-3 md:text-base text-xs resize-none font-light mb-1 text-center',
            )}
          />
        </div>
      </div>
    );
  }

  if (tile.static && !isEditing) {
    return (
      <div
        className="flex flex-col items-center justify-center h-full w-full"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleStartEdit();
        }}
      >
        <SocialMediaIconsList />
        <p className="mt-3 text-sm font-semibold">Add social</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex flex-col items-center justify-center w-full h-full p-6 rounded-2xl overflow-hidden',
        currentStyles.background,
        currentStyles.textColor,
        { 'justify-end': is2x2 },
        { 'flex-row gap-14': is2x1 },
      )}
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest('[data-link]')) return;
        if (isMyProfile) onMouseDown(e);
      }}
      onTouchStart={(e) => {
        if ((e.target as HTMLElement).closest('[data-link]')) return;
        if (isMyProfile) {
          if (touchedTileId !== tile.i) {
            e.preventDefault();
            return;
          }
          onTouchStart(e);
        }
      }}
    >
      <div
        className={cn('flex items-center justify-center mb-4', {
          'mb-0': is2x1,
        })}
      >
        <Icon
          name={`${tile.iconName}-white`}
          className={cn('size-24 text-white filter drop-shadow-sm', {
            'size-60': is2x2,
            'size-32': is2x1,
          })}
        />
      </div>

      <div className="text-center">
        <h3
          data-link
          onClick={(e) => {
            e.stopPropagation();
            window.open(tile.content as string, '_blank');
          }}
          className={cn(
            'text-xs! font-medium hover:underline cursor-pointer',
            currentStyles.textColor,
            {
              'md:text-base!': is1x1 && !isMobileView,
              'md:text-lg!': !isMobileView,
            },
          )}
        >
          {tileTitle}
        </h3>
      </div>
    </div>
  );
};
